import { ReactNode } from "react";
import { motion } from "framer-motion";

/* ============================================
   Section Wrapper
   ============================================ */

export function Section({
  children,
  className = "",
  dark = false,
}: {
  children: ReactNode;
  className?: string;
  dark?: boolean;
}) {
  return (
    <section
      className={`py-20 md:py-28 ${
        dark ? "bg-[var(--color-midnight)] text-[var(--color-cream)]" : ""
      } ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

/* ============================================
   Ornamental Divider
   ============================================ */

export function OrnamentDivider({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-4 my-8 ${className}`}>
      <div className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-transparent to-[var(--color-gold)]/40" />
      <svg viewBox="0 0 24 24" className="w-5 h-5 text-[var(--color-gold)]/50">
        <path
          fill="currentColor"
          d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
        />
      </svg>
      <div className="h-px flex-1 max-w-[120px] bg-gradient-to-l from-transparent to-[var(--color-gold)]/40" />
    </div>
  );
}

/* ============================================
   Section Heading
   ============================================ */

export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  center = true,
  dark = false,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  center?: boolean;
  dark?: boolean;
}) {
  return (
    <div className={center ? "text-center" : ""}>
      {eyebrow && (
        <span className="inline-block text-[var(--color-gold)] text-sm font-semibold uppercase tracking-[0.15em] mb-3">
          {eyebrow}
        </span>
      )}
      <h2
        className={`font-[var(--font-display)] text-3xl md:text-4xl lg:text-5xl font-bold leading-tight ${
          dark ? "text-[var(--color-cream)]" : "text-[var(--color-midnight)]"
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={`mt-4 text-lg max-w-2xl leading-relaxed ${center ? "mx-auto" : ""} ${
            dark ? "text-[var(--color-cream)]/60" : "text-[var(--color-ink-muted)]"
          }`}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* ============================================
   Animated Card
   ============================================ */

export function AnimatedCard({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ============================================
   Badge / Tag
   ============================================ */

const badgeColors: Record<string, string> = {
  // Seerah eras
  pre_prophethood: "bg-[var(--color-ink-muted)]/10 text-[var(--color-ink-muted)]",
  meccan_early: "bg-amber-50 text-amber-700",
  meccan_middle: "bg-orange-50 text-orange-700",
  meccan_late: "bg-red-50 text-red-700",
  medinan_early: "bg-emerald-50 text-emerald-700",
  medinan_middle: "bg-teal-50 text-teal-700",
  medinan_late: "bg-cyan-50 text-cyan-700",
  post_conquest: "bg-[var(--color-gold)]/10 text-[var(--color-gold-dim)]",
  // Categories
  revelation: "bg-purple-50 text-purple-700",
  battle: "bg-red-50 text-red-700",
  treaty: "bg-blue-50 text-blue-700",
  migration: "bg-teal-50 text-teal-700",
  social: "bg-pink-50 text-pink-700",
  legislation: "bg-indigo-50 text-indigo-700",
  miracle: "bg-amber-50 text-amber-700",
  personal: "bg-rose-50 text-rose-700",
  death: "bg-gray-100 text-gray-600",
  // Civilizations
  byzantine: "bg-purple-50 text-purple-700",
  sassanid: "bg-red-50 text-red-700",
  tang_china: "bg-amber-50 text-amber-700",
  sui_china: "bg-yellow-50 text-yellow-700",
  axumite: "bg-green-50 text-green-700",
  visigothic: "bg-stone-100 text-stone-700",
  frankish: "bg-blue-50 text-blue-700",
  indian: "bg-orange-50 text-orange-700",
  turkic: "bg-sky-50 text-sky-700",
  lakhmid: "bg-rose-50 text-rose-700",
  ghassanid: "bg-teal-50 text-teal-700",
  himyarite: "bg-emerald-50 text-emerald-700",
  multiple: "bg-gray-100 text-gray-700",
  // Source types
  quran: "bg-emerald-50 text-emerald-700",
  hadith_sahih: "bg-blue-50 text-blue-700",
  hadith_hasan: "bg-cyan-50 text-cyan-700",
  // Severity
  low: "bg-green-50 text-green-700",
  medium: "bg-yellow-50 text-yellow-700",
  high: "bg-orange-50 text-orange-700",
  critical: "bg-red-50 text-red-700",
  // Status
  completed: "bg-emerald-50 text-emerald-700",
  sajdah: "bg-purple-50 text-purple-700",
};

export function Badge({
  value,
  className = "",
}: {
  value: string | null | undefined;
  className?: string;
}) {
  if (!value) return null;
  const colorClass = badgeColors[value] || "bg-gray-100 text-gray-700";
  const label = value.replace(/_/g, " ");
  return (
    <span
      className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${colorClass} ${className}`}
    >
      {label}
    </span>
  );
}

/* ============================================
   Loading Skeleton
   ============================================ */

export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-[var(--color-cream-dark)] rounded ${className}`}
    />
  );
}

export function CardSkeleton() {
  return (
    <div className="card-elevated p-6">
      <Skeleton className="h-4 w-24 mb-3" />
      <Skeleton className="h-6 w-full mb-2" />
      <Skeleton className="h-4 w-3/4 mb-4" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}
